import React, { useState, useEffect, useCallback, Fragment } from 'react';
import { db, storage } from './firebase-config';
import { doc, setDoc, getDoc, updateDoc, serverTimestamp, onSnapshot } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import { adminServices } from './services/adminServices';
import { geminiGenerateText } from './services/geminiService';
import { getAiPipelineMode } from './services/aiPipelineSettings';
import { isSchemaV1Pipeline, isSchemaV2Pipeline } from './constants/aiPipelineModes';
import { buildSchemaV1SefDiagnosticPrompt } from './services/aiPipelines/schemaV1';
import GuidedTour from './GuidedTour';
import Questionnaire from './Questionnaire';
import './admin-styles.css';
import AccreditationHub from './AccreditationHub';
import { en as enMaster } from './locales/en';
import { localizationService } from './services/localizationService';
import { languageManifest as baseLanguageManifest } from './locales';
import { useManagedLanguages } from './hooks/useManagedLanguages';
import { getTranslatingIndicator } from './locales/translatingIndicator';
import { AccessibilityControls } from './components/AccessibilityControls';
import PortalSchoolLogo from './components/PortalSchoolLogo';
import { LevelMaturityTooltip } from './components/LevelMaturityTooltip';
import SupportHelpButton from './components/SupportHelpButton';
import SupportIssueModal from './components/SupportIssueModal';
import {
  A11Y_STORAGE_KEY,
  getInitialA11yScheme,
  getInitialHoverZoomOthers,
  syncHoverZoomOthersToDom,
} from './accessibility/a11yPreferences';
import { portalDrawerOffscreenEdgeCss, portalDrawerWidthCss } from './constants/portalLayout';

/** Aligns with SEF mobile overlay (`app.css` .audit-portal--mobile-sef-block). */
const NARROW_VIEWPORT_MQ = '(max-width: 1023px)';

const APP_UI_EN = {
  a11y_zoom_section_title: "Zoom",
  a11y_panel_aria_split: "Accessibility: zoom and colour scheme",
  btn_back_dashboard: "Dashboard",
  msg_no_id_found: "Please use your institutional Magic Link or register to begin.",
  btn_go_to_registration: "Register for Accreditation",
  btn_go_to_renewal: "Renewal / upgrade (existing school)",
  t5_title: "Renewals and upgrades",
  t5_desc: "Apply for a new accreditation cycle or a higher level — without repeating full registration.",
  t5_cta: "Apply",
  t2_desc_desktop_note: "Desktop/tablet only.",
  a11y_btn: "Accessibility",
  a11y_panel_title: "Colour scheme",
  a11y_scheme_default: "Default theme",
  a11y_scheme_wb: "White text on black",
  a11y_scheme_cg: "Black text on white",
  a11y_scheme_nb: "Navy on pale blue",
  a11y_scheme_sol: "Teal on cream",
  a11y_hover_zoom_others_label: "Hover zoom on other controls",
  a11y_hover_zoom_others_hint:
    "When this is on, the language selector, report links, dashboard tiles, header buttons, and side drawer tabs use the same large hover zoom as the status badges.",
  help_btn: "Help",
};

/** Maps localized sync strings to stable CSS tone classes (avoids broken classes like "auto saving..."). */
function getSyncIndicatorTone(status) {
  const t = String(status || '').toLowerCase();
  if (t.includes('error')) return 'error';
  if (t.includes('deleting')) return 'saving';
  if (t.includes('deleted') && !t.includes('error')) return 'deleted';
  if (t.includes('final')) return 'finalized';
  if (t.includes('saving') || t.includes('uploading') || t.includes('upload')) return 'saving';
  return 'saved';
}

/** Registration form sections are reordered over time — resolve questions by key, not section index. */
function findRegistrationQuestion(accreditationForm, questionKey) {
  for (const section of accreditationForm?.sections || []) {
    const question = section?.questions?.[questionKey];
    if (question) return question;
  }
  return null;
}

function PortalHeaderGlobeIcon(props) {
  return (
    <svg
      width={18}
      height={18}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      focusable="false"
      {...props}
    >
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.65" />
      <path
        d="M2 12h20M12 2c2.8 4 4.2 7.2 4.2 10S14.8 16 12 20c-2.8-4-4.2-7.2-4.2-10S9.2 6 12 2z"
        stroke="currentColor"
        strokeWidth="1.65"
        strokeLinecap="round"
      />
    </svg>
  );
}

function PortalHeaderFlagIcon(props) {
  return (
    <svg
      width={18}
      height={18}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      focusable="false"
      {...props}
    >
      <path
        d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"
        stroke="currentColor"
        strokeWidth="1.65"
        strokeLinejoin="round"
      />
      <line x1="4" y1="22" x2="4" y2="15" stroke="currentColor" strokeWidth="1.65" strokeLinecap="round" />
    </svg>
  );
}

function App() {
  const { languageManifest } = useManagedLanguages();
  const [a11yPanelOpen, setA11yPanelOpen] = useState(false);
  const [a11yScheme, setA11yScheme] = useState(getInitialA11yScheme);
  const [hoverZoomOthers, setHoverZoomOthers] = useState(getInitialHoverZoomOthers);
  const [hasEntered, setHasEntered] = useState(false);
  const [portalView, setPortalView] = useState('DASHBOARD');
  const [sidebarTab, setSidebarTab] = useState('guidance');
  // Helper to determine starting language: Manual Choice > Laptop Detection > Default
  const getInitialLanguage = () => {
    const saved = localStorage.getItem('ifip_lang');
    if (saved && baseLanguageManifest[saved]) return saved;

    const browserLang = navigator.language.split('-')[0]; 
    if (baseLanguageManifest[browserLang]) return browserLang;

    return 'en'; 
  };

  const [langKey, setLangKey] = useState(getInitialLanguage());

  const [activeStationIndex, setActiveStationIndex] = useState(0);
  const [hoveredLevelTip, setHoveredLevelTip] = useState(null);
  const [pinnedLevelTip, setPinnedLevelTip] = useState(null);
  const [openPanel, setOpenPanel] = useState(null); // 'guidance', 'profile', or null
  const [isNarrowViewport, setIsNarrowViewport] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(NARROW_VIEWPORT_MQ).matches
  );
  const [isReviewMode, setIsReviewMode] = useState(false);
  const [syncStatus, setSyncStatus] = useState(enMaster.ui.sync_saved);
  
  const [auditData, setAuditData] = useState({
  stations: {},
  evidenceVault: []
});
  const [auditStatus, setAuditStatus] = useState('Draft'); 
  const [report, setReport] = useState(null);
  const [showQuestionnaire, setShowQuestionnaire] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [diagnosticFeedback, setDiagnosticFeedback] = useState([]);
  const [isAiAnalyzing, setIsAiAnalyzing] = useState(false);
  const [pendingDeleteName, setPendingDeleteName] = useState(null);
  const [isTourOpen, setIsTourOpen] = useState(false);
  const [showSefSuccess, setShowSefSuccess] = useState(false); // Added for design-led success message // Added
  const [showSupportModal, setShowSupportModal] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const mq = window.matchMedia(NARROW_VIEWPORT_MQ);
    const onChange = () => setIsNarrowViewport(mq.matches);
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', onChange);
      else mq.removeListener(onChange);
    };
  }, []);

  useEffect(() => {
    if (isNarrowViewport && isTourOpen) {
      setIsTourOpen(false);
    }
  }, [isNarrowViewport, isTourOpen]);

  // Auto-start tour if first time (desktop / large tablet only — tour targets wide SEF layout)
  useEffect(() => {
    const hasSeenTour = localStorage.getItem('ifip_tour_complete');
    if (hasEntered && portalView === 'SEF' && !hasSeenTour && !isNarrowViewport) {
      setIsTourOpen(true);
    }
  }, [hasEntered, portalView, isNarrowViewport]);

  useEffect(() => {
    setHoveredLevelTip(null);
    setPinnedLevelTip(null);
  }, [activeStationIndex]);

  useEffect(() => {
    if (pinnedLevelTip == null) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        setPinnedLevelTip(null);
        setHoveredLevelTip(null);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [pinnedLevelTip]);

   // Initialise with enMaster to ensure the app always has data and never crashes
  const [activeLang, setActiveLang] = useState(enMaster); 
  /** Ref-counted: portal sync + hub sync + React Strict Mode (overlapping runs) without clearing early. */
  const [translatingDepth, setTranslatingDepth] = useState(0);
  const isGlobalTranslating = translatingDepth > 0;

  const registerTranslating = useCallback(() => {
    setTranslatingDepth((d) => d + 1);
    let finished = false;
    return () => {
      if (finished) return;
      finished = true;
      setTranslatingDepth((d) => Math.max(0, d - 1));
    };
  }, []);

  // Define framework variables once here so they are available to all components below
  const stations = activeLang.stations;
  const currentStation = stations[activeStationIndex];

  useEffect(() => {
    const persisted = a11yScheme === 'bw' ? 'cg' : a11yScheme;
    const hoverZoomEffective = isNarrowViewport ? false : Boolean(hoverZoomOthers);
    try {
      localStorage.setItem(
        A11Y_STORAGE_KEY,
        JSON.stringify({ colorScheme: persisted, hoverZoomOthers: Boolean(hoverZoomOthers) })
      );
    } catch {
      // Ignore persistence failures.
    }
    document.documentElement.setAttribute('data-a11y-scheme', persisted);
    syncHoverZoomOthersToDom(hoverZoomEffective);
  }, [a11yScheme, hoverZoomOthers, isNarrowViewport]);

  useEffect(() => {
    const release = registerTranslating();
    const syncPortalContent = async () => {
      try {
        // 1. FAST HYDRATION (Check translations collection first)
        const storedUI = await localizationService.getStored(langKey, 'app_main', 'v2_translations');
        const storedForm = await localizationService.getStored(langKey, 'registration_form', 'v2_translations');
        
        if (storedUI && storedForm) {
          setActiveLang({ ...enMaster, ui: storedUI, accreditationForm: storedForm });
        } else if (storedUI?.localizing_indicator && langKey !== 'en') {
          setActiveLang((prev) => ({
            ...(prev || enMaster),
            ui: { ...(prev?.ui || enMaster.ui), localizing_indicator: storedUI.localizing_indicator },
          }));
        } else if (langKey !== 'en') {
          const instant = getTranslatingIndicator(langKey);
          setActiveLang((prev) => ({
            ...(prev || enMaster),
            ui: { ...(prev?.ui || enMaster.ui), localizing_indicator: instant },
          }));
        }

        // 2. BACKGROUND SYNC (Living Registry + AI Delta)
        const uiChunk = await localizationService.sync(langKey, 'app_main', { ...enMaster.ui, ...APP_UI_EN }, 'v2_master_en', 'v2_translations');
        const formChunk = await localizationService.sync(langKey, 'registration_form', enMaster.accreditationForm, 'v2_master_en', 'v2_translations');
        
        setActiveLang(prev => ({ ...(prev || enMaster), ui: uiChunk, accreditationForm: formChunk }));

        // 3. DRIP-FEED FRAMEWORK (Isolated Stations Collection)
        const translatedStations = [];
        for (const station of enMaster.stations) {
          const sDocId = `station_${station.id}`;
          const sSync = await localizationService.sync(langKey, sDocId, station, 'v2_master_en', 'v2_framework_stations');
          translatedStations.push(sSync);
          
          setActiveLang(prev => ({
            ...prev,
            stations: [...translatedStations, ...enMaster.stations.slice(translatedStations.length)]
          }));
        }
      } catch (err) {
        console.error("Portal Hydration Error:", err);
      } finally {
        release();
      }
    };

    syncPortalContent();
    return () => {
      release();
    };
  }, [langKey, registerTranslating]);

// Initialize Gemini via server-side callable (no browser API key)

// Helper to strip AI bullets and handle bolding
  const formatAiText = (text) => {
    // 1. Remove leading bullets/numbers often returned by AI
    const cleaned = text.replace(/^[\*\-\d\.\s]+/, "");
    // 2. Split and wrap **text** in <strong> tags
    return cleaned.split(/(\*\*.*?\*\*)/g).map((part, i) => 
      part.startsWith("**") && part.endsWith("**") 
        ? <strong key={i} style={{ color: '#138a8a' }}>{part.slice(2, -2)}</strong> 
        : part
    );
  };
	
    
  // This happens instantly when the page loads
  const urlParams = new URLSearchParams(window.location.search);
  const [schoolId] = useState(urlParams.get('sid'));
  const [schoolName, setSchoolName] = useState("");
  const [schoolProfile, setSchoolProfile] = useState(null);
  const sefChoicePending = schoolProfile?.renewalRequest?.sefChoice === 'pending';
  const sefReadOnly = auditStatus === 'Submitted' || auditStatus === 'Finalised' || sefChoicePending;
  const [sefChoiceBusy, setSefChoiceBusy] = useState(false);
  const [editingField, setEditingField] = useState(null); // Tracks which key (e.g., 'q7') is active
  const [editValue, setEditValue] = useState(""); // Tracks temporary input value

    // 1. Add a helper function to toggle tags
  const updateFileTags = (fileName, targetStationId) => {
  if (sefReadOnly) return;
  setAuditData(prev => ({
    ...prev,
    evidenceVault: prev.evidenceVault.map(file => {
      if (file.name !== fileName) return file;
      const tags = file.tags || [];
      const newTags = tags.includes(targetStationId) 
        ? tags.filter(t => t !== targetStationId) 
        : [...tags, targetStationId];
      return { ...file, tags: newTags };
    })
  }));
};

  useEffect(() => {
    localStorage.setItem('ifip_lang', langKey);
  }, [langKey]);

  // --- BROWSER HISTORY NAVIGATION LOGIC ---
  useEffect(() => {
    if (!hasEntered) return;
    const state = { view: portalView, review: isReviewMode };
    // Only push if the state actually differs from current browser state
    if (window.history.state?.view !== portalView || window.history.state?.review !== isReviewMode) {
      window.history.pushState(state, '', '');
    }
  }, [portalView, isReviewMode, hasEntered]);

  useEffect(() => {
    const handlePopState = (event) => {
      if (event.state) {
        setPortalView(event.state.view || 'DASHBOARD');
        setIsReviewMode(event.state.review || false);
      } else {
        // Fallback to Dashboard if state is empty
        setPortalView('DASHBOARD');
        setIsReviewMode(false);
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);
  
  // Data Loading Logic (Unified Profile & Audit Fetch)
  useEffect(() => {
    if (!schoolId) return;

    // Locate the section where you fetch Audit Progress (Section B in your useEffect)

const loadFullContext = async () => {
    try {
      // A. Restore the Institutional Profile fetch
      const schoolRef = doc(db, "schools", schoolId);
      const schoolSnap = await getDoc(schoolRef);

      if (schoolSnap.exists()) {
        const data = schoolSnap.data();
        setSchoolName(data.officialName);
        setSchoolProfile(data);
        
        const hasManualChoice = localStorage.getItem('ifip_lang');
        const browserLang = navigator.language.split('-')[0];
        const globalLang = data.currentLanguage || data.signupLanguage;
        if (!hasManualChoice && !languageManifest[browserLang] && globalLang) {
          setLangKey(globalLang);
        }
      }

      // B. Fetch the Audit Progress
      const auditRef = doc(db, "audits", schoolId);
      const auditSnap = await getDoc(auditRef);
      if (auditSnap.exists()) {
        const dbData = auditSnap.data();
        setAuditData({
          stations: dbData.stations || {},
          evidenceVault: dbData.evidenceVault || []
        });
        setAuditStatus(dbData.status || 'Draft');
      }

      // C. Real-time Report Listener (For Dashboard status)
      const reportRef = doc(db, "reports", schoolId);
      const unsubReport = onSnapshot(reportRef, (snap) => {
        if (snap.exists()) setReport(snap.data());
      });

      setIsLoading(false); 
      return () => unsubReport(); // Cleanup listener
    } catch (err) {
      console.error("Critical Data Load Error:", err);
      setIsLoading(false); 
    }
  };

    loadFullContext();
  }, [schoolId]);

  // Keep the school's global language preference in sync with the portal selector.
  // Uses currentLanguage (not signupLanguage) so registration language remains historical.
  useEffect(() => {
    if (!schoolId || !langKey || isLoading) return;
    const persisted = String(schoolProfile?.currentLanguage || '').trim().toLowerCase();
    if (persisted === langKey) return;

    const syncLanguagePreference = async () => {
      try {
        const schoolRef = doc(db, "schools", schoolId);
        await updateDoc(schoolRef, {
          currentLanguage: langKey,
          lastLanguageUpdateAt: serverTimestamp()
        });
        setSchoolProfile(prev => ({ ...(prev || {}), currentLanguage: langKey }));
      } catch (err) {
        console.error("Failed to persist school language preference:", err);
      }
    };

    syncLanguagePreference();
  }, [schoolId, langKey, isLoading, schoolProfile?.currentLanguage]);

  // 3. Update Autosave Logic
useEffect(() => {
  if (isLoading || sefReadOnly) return;

  const timer = setTimeout(async () => {
    setSyncStatus(activeLang.ui.sync_saving);
    try {
      const auditRef = doc(db, "audits", schoolId);
      await setDoc(auditRef, {
        stations: auditData.stations,
        evidenceVault: auditData.evidenceVault,
        status: 'Draft', // MUST stay as Draft here
        lastUpdated: serverTimestamp()
      }, { merge: true });
      setSyncStatus(activeLang.ui.sync_saved);
    } catch (err) { 
      setSyncStatus(activeLang.ui.sync_error); 
    }
  }, 1500); // This was missing

  return () => clearTimeout(timer);
}, [auditData, schoolId, isLoading, sefReadOnly, activeLang.ui]);

  // PHASE B: LIVE AI DIAGNOSTIC ENGINE
   // --- PATCH START: STEP 4 (Fully Localized AI & Review) ---

// 1. Fully Localized AI Diagnostic Function
const runAiDiagnostic = async () => {
  setIsAiAnalyzing(true);
  setDiagnosticFeedback([]);

  try {
    // A. Context Mapping (Using Translated Labels)
    const profileContext = Object.entries(schoolProfile?.answers || {}).map(([key, value]) => {
      let label = key;
      activeLang.accreditationForm.sections.forEach(section => {
        if (section.questions[key]) label = section.questions[key].label;
      });
      return `${label}: ${typeof value === 'object' ? JSON.stringify(value) : value}`;
    }).join("\n");

    // B. Evidence Mapping (Localized reference)
    const evidenceInventory = stations.map(s => {
      const linkedFiles = auditData.evidenceVault?.filter(f => f.tags?.includes(s.id)) || [];
      const fileNames = linkedFiles.map(f => f.name).join(", ") || "---";
      return `${activeLang.ui.nav_station_prefix} ${s.id}: [${fileNames}]`;
    }).join("\n");

    // C. Narratives
    const auditSummary = stations.map(s => {
      const data = auditData.stations[s.id] || { selectedLevel: 1, narrative: "" };
      return `${activeLang.ui.nav_station_prefix} ${s.id} (${activeLang.ui.label_level_prefix} ${data.selectedLevel}):\n${data.narrative}`;
    }).join("\n---\n");

    // D. Prompt — legacy peer-coaching OR schema_v1/v2 verification stance for school (titles only)
    const pipelineMode = await getAiPipelineMode();
    const useVerificationPrompt =
      isSchemaV1Pipeline(pipelineMode) || isSchemaV2Pipeline(pipelineMode);
    const prompt = useVerificationPrompt
      ? buildSchemaV1SefDiagnosticPrompt({
          schoolName,
          languageNativeName: languageManifest[langKey].nativeName,
          profileContext,
          evidenceInventory,
          auditSummary,
        })
      : `
        You are the Senior Strategic Lead for the International Forum of Inclusion Practitioners (IFIP).
        Your task is to provide a "Holistic Correlation Analysis" for ${schoolName}.

        CRITICAL: Response must be entirely in ${languageManifest[langKey].nativeName}.

        MANDATORY START: Your response MUST begin with the following disclaimer to explain that you acknowledge the uploaded evidence, but as an AI, you have not reviewed the actual contents of the documents. A human IFIP auditor will conduct the qualitative verification.

        STEP 1: INSTITUTIONAL DNA (From Registration Data)
        ${profileContext}

        STEP 2: EVIDENCE INVENTORY (Filenames and Station Mapping)
        ${evidenceInventory}

        STEP 3: AUDIT NARRATIVES (Self-Assessment)
        ${auditSummary}

        YOUR ANALYSIS GOALS (Strategic Peer Review):
        
        1. PROPORTIONALITY & FEASIBILITY: 
           - Analyze the relationship between the "Hard Data" (Headcounts, Needs, Staffing) and the "Narrative Claims."
           - Lateral Thinking: Does the intensity of the "Lived Experience" described seem achievable at the reported scale? If there is a logical friction (e.g., high-intensity support with low-intensity staffing), ask for the "secret sauce"—the specific method that makes it possible.

        2. THE GOLDEN THREAD (Consistency): 
           - Does the "Why" (Motivation/Vision in Step 1) actually show up in the "How" (Methods/Resources in Step 3)? 
           - Look for alignment between their institutional identity and their maturity level.

        3. EVIDENCE MAPPING LOGIC:
           - I have provided you with a list of filenames and which stations they are tagged to.
   - Analyze if the user has provided a variety of documents to support their narrative across the stations.
   - If a station has a high Maturity Level (4 or 5) but has zero documents linked, you must flag this as a "Narrative-Evidence Gap."
   - IMPORTANT DISCLAIMER: You MUST explicitly state that while you acknowledge the presence of these files, you have NOT reviewed the internal contents of the documents.

        4. MATURITY CALIBRATION (The "Modesty/Ambition" Gap):
           - Identify if the school is "underselling" themselves (describing Level 4 work but choosing Level 1) or if they are "aspirational" (choosing Level 5 but describing Level 2). Help them align their rating with their reality.

        5. HARD-DATA CONTRADICTION CHECK: 
           - Carefully compare all hard data, such as pupil and teacher numbers.
           - If there is a disparity or contradiction, you MUST flag this as a "Critical Narrative Conflict."
           - Direct Instruction: provide a clear response such as: "You have recorded [data], but your narrative describes a different picture. This is likely to be raised as a concern by the IFIP reviewer. Please correct the data or the narrative."

        STRICT PHILOSOPHY:
        - Act as a "Senior Strategic Peer." Use professional warmth.
        - Instead of "finding errors," identify "areas of narrative friction."
        - Phrase feedback as: "To make your submission undeniable for the IFIP board, you might clarify the relationship between [Point A] and [Point B]..."
        - Avoid "Compliance language" (e.g., don't say "You failed to provide X"). 
        - Instead use "Growth language" (e.g., "To make this station even more undeniable, you might consider...").
        - Assume the user is an expert.
        - If you find a friction point, phrase it constructively: "There appears to be a contradiction between the data and narrative in [x], you might clarify how..."

        OUTPUT FORMAT:
        Provide 5-7 actionable, bulleted points. Start with a warm opening acknowledging their specific institutional context (e.g., "With ${schoolProfile?.answers?.q7 || 'your'} students, your commitment is clear...").
      `;

    const responseText = await geminiGenerateText(prompt);
    const tips = responseText.split("\n").filter(line => line.trim().length > 5);
    setDiagnosticFeedback(tips);

  } catch (err) {
    // Falls back to UI language key for error
    setDiagnosticFeedback([activeLang.ui.sync_error || "Error"]);
  } finally {
    setIsAiAnalyzing(false);
  }
};


  useEffect(() => {
    if (isReviewMode) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      runAiDiagnostic();
    }
  }, [isReviewMode]);

  // --- HYDRATION GUARD ---
  // This must be placed after all React hooks (useState/useEffect) 
  // but before any code that tries to access activeLang properties.
  if (!activeLang) {
    return (
      <div className="splash-wrapper">
        <div className="translating-dots" style={{ color: 'var(--a11y-page-text)', letterSpacing: '2px', fontSize: '1.2rem', fontWeight: '300' }}>
          {langKey === 'en' ? enMaster.ui.portal_boot_loading_en : enMaster.ui.portal_boot_loading_sync}
        </div>
      </div>
    );
  }

  const stationState = auditData.stations[currentStation.id] || { 
  selectedLevel: 1, 
  narrative: "" 
};
  const levelData = currentStation.diagnosticGrid[stationState.selectedLevel];

  const isLevelTipVisible = (num) =>
    pinnedLevelTip === num || (hoveredLevelTip === num && pinnedLevelTip === null);

  // 1. Updated Narrative/Level Update Function
const handleUpdate = (updates) => {
  if (sefReadOnly) return;
  
  const stationId = currentStation.id;

  setAuditData(prev => ({
    ...prev,
    stations: {
      ...prev.stations,
      [stationId]: { 
        ...(prev.stations[stationId] || { selectedLevel: 1, narrative: "" }), 
        ...updates 
      }
    }
  }));
};

 // 2. Streamlined File Upload (AI REMOVED)
const handleFileUpload = async (e) => {
  if (sefReadOnly) return;
  const file = e.target.files[0];
  if (!file) return;
  
  // --- NEW: DUPLICATION CHECK ---
  const isDuplicate = auditData.evidenceVault.some(f => f.name === file.name);
  if (isDuplicate) {
    alert(`${file.name} ${activeLang.ui.alert_duplicate_file}`);
    e.target.value = null;
    return;
  }
  // ------------------------------

  const MAX_SIZE_MB = 15;
  if (file.size > MAX_SIZE_MB * 1024 * 1024) {
    const tooLarge = (activeLang.ui.alert_file_too_large || '').replace('{{maxMb}}', String(MAX_SIZE_MB));
    alert(tooLarge);
    return;
  }

  const looksLikePhoto =
    (file.type && String(file.type).startsWith('image/')) ||
    /\.(jpe?g|png|gif|webp|heic|heif|bmp|tiff?)$/i.test(file.name || '');
  if (looksLikePhoto) {
    const photoMsg =
      activeLang.ui.alert_photo_evidence_policy ||
      'This looks like a photograph or image. It will be stored securely and viewed only by your assigned IFIP accreditor. Photographs are never uploaded to AI. Continue?';
    const ok = window.confirm(photoMsg);
    if (!ok) {
      e.target.value = null;
      return;
    }
  }

  const stationId = currentStation.id;
  const storageRef = ref(storage, `evidence/${schoolId || "test"}/${file.name}`);
  const uploadTask = uploadBytesResumable(storageRef, file);

  setSyncStatus(activeLang.ui.sync_uploading);

  uploadTask.on('state_changed', 
    (snapshot) => {
      const progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
      setSyncStatus((activeLang.ui.sync_uploading_progress || '').replace('{{pct}}', String(progress)));
    }, 
    (error) => {
      console.error("Upload Error:", error);
      setSyncStatus(activeLang.ui.sync_upload_error);
    }, 
    async () => {
      const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
      
      const newFileEntry = { 
        name: file.name, 
        url: downloadURL,
        tags: [stationId], // Automatically tag to the current station
        size: typeof file.size === 'number' ? file.size : Number(file.size) || null,
        contentType: file.type || null,
        uploadedAt: new Date().toISOString()
      };

      // Push to the GLOBAL VAULT instead of the station
      setAuditData(prev => ({
        ...prev,
        evidenceVault: [...(prev.evidenceVault || []), newFileEntry]
      }));

      setSyncStatus(activeLang.ui.sync_saved);
      e.target.value = null; 
    }
  );
};

// 3. Simplified File Delete
const handleFileDelete = async (fileName) => {
  if (sefReadOnly) return;
  setSyncStatus(activeLang.ui.sync_deleting);

  try {
    const fileRef = ref(storage, `evidence/${schoolId}/${fileName}`);
    await deleteObject(fileRef);

    setAuditData(prev => ({
      ...prev,
      evidenceVault: (prev.evidenceVault || []).filter(f => f.name !== fileName)
    }));
    
    setSyncStatus(activeLang.ui.sync_deleted);
  } catch (err) {
    console.error("Delete error:", err);
    setSyncStatus(activeLang.ui.sync_delete_error);
  }
};

const applySefCarryChoice = async (choice) => {
  if (!schoolId || sefChoiceBusy) return;
  setSefChoiceBusy(true);
  try {
    const auditRef = doc(db, "audits", schoolId);
    if (choice === 'blank') {
      const cleared = { stations: {}, evidenceVault: [] };
      setAuditData(cleared);
      await setDoc(auditRef, {
        ...cleared,
        status: 'Draft',
        lastUpdated: serverTimestamp(),
      }, { merge: true });
    } else {
      await setDoc(auditRef, {
        status: 'Draft',
        lastUpdated: serverTimestamp(),
      }, { merge: true });
    }
    setAuditStatus('Draft');
    await updateDoc(doc(db, "schools", schoolId), {
      'renewalRequest.sefChoice': choice,
    });
    setSchoolProfile((prev) => ({
      ...(prev || {}),
      renewalRequest: { ...(prev?.renewalRequest || {}), sefChoice: choice },
    }));
  } catch (err) {
    console.error('SEF carry choice failed:', err);
    setSyncStatus(activeLang.ui.sync_error);
  } finally {
    setSefChoiceBusy(false);
  }
};

const submitFinalAudit = async () => {
    if (sefChoicePending || auditStatus === 'Finalised') return;
    const confirmSubmit = window.confirm(activeLang.ui.confirm_modal_alert);
    if (!confirmSubmit) return;

    setSyncStatus(activeLang.ui.sync_saving);
    try {
      const auditRef = doc(db, "audits", schoolId);
      await setDoc(auditRef, {
        stations: auditData.stations,      // Use nested stations
        evidenceVault: auditData.evidenceVault, // Include the vault
        status: 'Submitted',
        submittedAt: serverTimestamp()
      }, { merge: true });

      try {
        await adminServices.markSchoolSefSubmitted(schoolId);
      } catch (submitErr) {
        await adminServices.initializeReport(schoolId);
      }

      setAuditStatus('Submitted');
      setIsReviewMode(false);
      setPortalView('DASHBOARD'); // Return to dashboard in the background // Added
      setShowSefSuccess(true); // Trigger the designer success overlay // Added
      setSyncStatus(activeLang.ui.sync_finalized);
    } catch (err) { setSyncStatus(activeLang.ui.sync_error); }
  };

  const isRTL = languageManifest[langKey].dir === 'rtl';

  const getA11yLabels = () => {
    const u = activeLang.ui || {};
    return {
      btn: u.a11y_btn || APP_UI_EN.a11y_btn,
      panelTitle: u.a11y_panel_title || APP_UI_EN.a11y_panel_title,
      panelAria: u.a11y_panel_title || APP_UI_EN.a11y_panel_title,
      panelAriaSplit: u.a11y_panel_aria_split || APP_UI_EN.a11y_panel_aria_split,
      zoomSectionTitle: u.a11y_zoom_section_title || APP_UI_EN.a11y_zoom_section_title,
      schemeDefault: u.a11y_scheme_default || APP_UI_EN.a11y_scheme_default,
      schemeWb: u.a11y_scheme_wb || APP_UI_EN.a11y_scheme_wb,
      schemeCg: u.a11y_scheme_cg || APP_UI_EN.a11y_scheme_cg,
      schemeNb: u.a11y_scheme_nb || APP_UI_EN.a11y_scheme_nb,
      schemeSol: u.a11y_scheme_sol || APP_UI_EN.a11y_scheme_sol,
      hoverZoomOthersLabel: u.a11y_hover_zoom_others_label || APP_UI_EN.a11y_hover_zoom_others_label,
      hoverZoomOthersHint: u.a11y_hover_zoom_others_hint || APP_UI_EN.a11y_hover_zoom_others_hint,
    };
  };

  const langSelectAria = activeLang.ui.nav_lang_select || activeLang.ui.grid_labels?.language || 'Language';
  const supportContext = {
    route: typeof window !== 'undefined' ? `${window.location.pathname}${window.location.search}` : '/',
    actorType: 'school_portal_user',
    schoolId,
    schoolName,
    workflowStep: schoolProfile?.workflowStep || null,
    qaPhase: report?.meta?.qaPhase || schoolProfile?.qaPhase || null,
    language: langKey,
    portalView,
    view: showQuestionnaire ? 'QUESTIONNAIRE' : portalView,
    isReviewMode,
    auditStatus,
  };

  const renderSplashInlineToolbar = (panelId) => (
    <div className="ifip-splash-toolbar-row">
      <AccessibilityControls
        panelOpen={a11yPanelOpen}
        setPanelOpen={setA11yPanelOpen}
        scheme={a11yScheme}
        setScheme={setA11yScheme}
        labels={getA11yLabels()}
        rootClassName="ifip-a11y-control-root ifip-a11y-control-root--splash-inline-toolbar"
        panelId={panelId}
        splashLangSelect={
          <select
            className="ifip-splash-lang-select"
            value={langKey}
            onChange={(e) => setLangKey(e.target.value)}
            aria-label={langSelectAria}
          >
            {Object.entries(languageManifest)
              .sort((a, b) =>
                (a[1].nativeName || a[1].label || a[0]).localeCompare(
                  b[1].nativeName || b[1].label || b[0]
                )
              )
              .map(([key, lang]) => (
                <option key={key} value={key} style={{ color: 'black' }}>
                  {lang.nativeName}
                </option>
              ))}
          </select>
        }
      />
      <SupportHelpButton
        label={activeLang.ui.help_btn || APP_UI_EN.help_btn}
        onClick={() => setShowSupportModal(true)}
      />
    </div>
  );

  if (isLoading && hasEntered) {
    return (
      <Fragment>
        <div className="loading-overlay">
          {activeLang.ui.loading_msg}
          <div className="ifip-loading-splash-toolbar">{renderSplashInlineToolbar('a11y-panel-app-loading')}</div>
        </div>
      </Fragment>
    );
  }
    const Dashboard = () => {
    const sefCount = auditData.stations ? Object.keys(auditData.stations).length : 0;
    const t = activeLang.ui;
    
    const sefLabel = auditStatus === 'Submitted' ? (t.t2_status_submitted || "Submitted") : 
                    sefCount === 7 ? (t.t2_status_ready || "Ready to Submit") : 
                    sefCount > 0 ? `${t.t2_status_drafting || "Drafting"} (${sefCount}/7)` : (t.t2_status_not_started || "Not Started");

    const lastSync = schoolProfile?.lastForensicUpdate?.toDate();
    const currentYear = new Date().getFullYear(); // Added
    const isCompleteThisYear = lastSync && lastSync.getFullYear() === currentYear; // Added
    const isStale = lastSync ? (new Date() - lastSync) > (10 * 30 * 24 * 60 * 60 * 1000) : true;
    
    const forensicLabel = isCompleteThisYear ? `${t.t3_status_synced || "Data Synced"} ${currentYear}` : // Added
                         !lastSync ? (t.t3_status_start || "Complete Optional Questionnaire") : 
                         isStale ? (t.t3_status_update || "Update Due") : 
                         `${t.t3_status_synced || "Synced"} ${lastSync.toLocaleDateString('default', { month: 'short', year: 'numeric' })}`;
    const isFactCheck = schoolProfile?.workflowStep === 'fact_check';
    const reportLabel = isFactCheck ? (t.t4_status_action || "Action: Please review your draft report") : (t.t4_status_view || "View reports");

    return (
      <div className="dashboard-container-v2">
        
        <div className="school-dashboard-grid school-dashboard-grid--matrix">
          <div className="portal-tile" onClick={() => setOpenPanel(openPanel === 'profile' ? null : 'profile')}>
            <div className="tile-icon">📋</div>
            <h3>{t.t1_title || "My Registration Form"}</h3>
            <p>{t.t1_desc || "Your registration form is locked, but you may still update key information in the School Profile panel"}</p>
            <div className="tile-status status-complete">{t.t1_status || "View / update"}</div>
          </div>

          <div className="portal-tile" onClick={() => setPortalView('SEF')}>
            <div className="tile-icon">⚖️</div>
            <h3>{t.t2_title || "My Self-Evaluation Form"}</h3>
            <p>
              {t.t2_desc || "Assess your institution against IFIP's 7-Station framework."}{' '}
              <span className="portal-tile__desktop-only-note">
                {t.t2_desc_desktop_note || APP_UI_EN.t2_desc_desktop_note}
              </span>
            </p>
            <div className={`tile-status ${auditStatus === 'Submitted' ? 'status-complete' : 'status-pending'}`}>
              {sefLabel}
            </div>
          </div>

          <div
            className="portal-tile"
            onClick={isCompleteThisYear ? null : () => setShowQuestionnaire(true)}
            style={{ cursor: isCompleteThisYear ? 'default' : 'pointer' }}
          >
            <div className="tile-icon">📊</div>
            <h3>{t.t3_title || "My Questionnaire"}</h3>
            <p>{t.t3_desc || "Optional annual questionnaire to support IFIP's global research"}</p>
            <div className={`tile-status ${isCompleteThisYear || !isStale ? 'status-complete' : (lastSync ? 'status-active' : 'status-pending')}`}>
              {forensicLabel}
            </div>
          </div>

          <div className="portal-tile-stack">
            <div
              className="portal-tile portal-tile--stacked"
              role="button"
              tabIndex={0}
              onClick={() => setPortalView('REPORTS')}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setPortalView('REPORTS');
                }
              }}
            >
              <div className="tile-icon">🏆</div>
              <h3>{t.t4_title || "My Accreditation Reports"}</h3>
              <div className={`tile-status ${isFactCheck ? 'status-active' : 'status-complete'}`}>
                {reportLabel}
              </div>
            </div>

            <div
              className="portal-tile portal-tile--stacked"
              role="button"
              tabIndex={0}
              onClick={() => { window.location.href = `/renewal?sid=${encodeURIComponent(schoolId)}`; }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  window.location.href = `/renewal?sid=${encodeURIComponent(schoolId)}`;
                }
              }}
            >
              <div className="tile-icon">⬆️</div>
              <h3>{t.t5_title || APP_UI_EN.t5_title}</h3>
              <div className="tile-status status-active">{t.t5_cta || APP_UI_EN.t5_cta}</div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  
  if (!hasEntered) {
    return (
      <Fragment>
      <div className="splash-wrapper">
	  <div className="word-container">
          <div className="letter">I</div>
          <div className="letter">F</div>
          <div className="i-column">
            <div className="zing-flare"></div>
            <img 
              src="/images/01%20IFIP%20Logo.png" 
              className="globe-dot" 
              alt={activeLang.ui.img_alt_ifip_globe} 
            />
            <span className="squash-i">I</span>
          </div>
          <div className="letter">P</div>
        </div>

        <div className="entrance-controls">
          <h2 style={{ 
            fontWeight: '300', 
            letterSpacing: '2px', 
            marginBottom: '30px', 
            color: 'var(--a11y-page-text)',
            opacity: 0.95
          }}>
            {activeLang.ui.nav_welcome} {schoolName ? (
               <span style={{ fontWeight: '800', color: 'var(--a11y-page-text)' }}> {schoolName}</span>
          ) : "!"}
          </h2>
          
          {/* CONTRADICTION FIXED: Handle the 'Security' state, then the 'Loading' state */}
          {!schoolId ? (
            <div style={{ textAlign: 'center', maxWidth: '520px', margin: '0 auto' }}>
              <p style={{ color: 'var(--a11y-page-text)', opacity: 0.85, fontSize: '0.95rem', lineHeight: '1.6', marginBottom: '20px' }}>
                {activeLang.ui.msg_no_id_found || "Access Restricted: Please use your institutional Magic Link or register to begin."}
              </p>
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', justifyContent: 'center' }}>
                <button
                  type="button"
                  onClick={() => { window.location.href = '/register'; }}
                  className="enter-button"
                  style={{
                    flex: '1 1 200px',
                    padding: '10px 16px',
                    fontSize: '0.9rem',
                    borderRadius: '10px',
                  }}
                >
                  {activeLang.ui.btn_go_to_registration || "Register for Accreditation"}
                </button>
                <button
                  type="button"
                  onClick={() => { window.location.href = '/renewal'; }}
                  className="back-button"
                  style={{
                    flex: '1 1 200px',
                    padding: '10px 16px',
                    fontSize: '0.9rem',
                    borderRadius: '10px',
                  }}
                >
                  {activeLang.ui.btn_go_to_renewal || APP_UI_EN.btn_go_to_renewal}
                </button>
              </div>
            </div>
          ) : !schoolProfile ? (
            <div style={{ color: 'var(--a11y-page-text)', opacity: 0.6, fontSize: '0.9rem', fontStyle: 'italic' }}>
              Syncing with IFIP Global...
            </div>
          ) : schoolProfile?.status === 'active' ? (
            <button 
              className="enter-button" 
              onClick={() => setHasEntered(true)}
              style={{
                padding: '15px 40px',
                borderRadius: '30px',
                fontSize: '1.1rem',
                fontWeight: 'bold',
                cursor: 'pointer',
                boxShadow: '0 10px 20px rgba(0,0,0,0.2)',
                transition: 'transform 0.2s ease'
              }}
              onMouseOver={(e) => e.target.style.transform = 'scale(1.05)'}
              onMouseOut={(e) => e.target.style.transform = 'scale(1)'}
            >
              {activeLang.ui.btn_enter_portal || "Enter Accreditation Portal"}
            </button>
          ) : (
            <div style={{
              background: 'rgba(209, 29, 126, 0.2)',
              border: '1px solid #d11d7e',
              padding: '20px',
              borderRadius: '12px',
              maxWidth: '400px',
              margin: '0 auto',
              color: 'var(--a11y-page-text)',
              fontSize: '0.9rem',
              lineHeight: '1.5'
            }}>
              {activeLang.ui.inactive_msg}
            </div>
          )}
          {renderSplashInlineToolbar('a11y-panel-app-splash')}
        </div>
      </div>
      </Fragment>
    );
  }

   const handleSchoolProfileUpdate = async (fieldKey, newValue) => {
    try {
      const schoolRef = doc(db, "schools", schoolId);
      // Construct the update object based on the nested structure of our registration data
      await updateDoc(schoolRef, {
        [`answers.${fieldKey}`]: newValue
      });
      
      // Update local state so the UI reflects changes immediately
      setSchoolProfile(prev => ({
        ...prev,
        answers: { ...prev.answers, [fieldKey]: newValue }
      }));
      
      setEditingField(null);
    } catch (err) {
      console.error("Error updating profile:", err);
      alert(activeLang.ui.alert_profile_save_failed);
    }
  };

  const sefMobileBlockActive = portalView === 'SEF' && !showQuestionnaire;

  return (
    <Fragment>
        <div
          className={`audit-portal${sefMobileBlockActive ? ' audit-portal--mobile-sef-block' : ''}`}
          dir={languageManifest[langKey].dir}
          style={{
            minHeight: '100vh',
            width: '100%',
            overflowX: 'visible',
            ['--portal-drawer-width']: portalDrawerWidthCss(),
            ['--portal-drawer-edge-shift']: openPanel ? portalDrawerWidthCss() : '0px',
          }}
        >
      {sefMobileBlockActive && (
      <div className="mobile-only-blocker" role="alertdialog" aria-modal="true" aria-labelledby="mobile-sef-blocker-title">
        <div className="mobile-only-blocker__inner">
          <img src="https://www.worldinclusioncongress.com/wp-content/uploads/2026/02/211123_IFIP-Logo-B_Rev-copy-e1772027071706.png" alt={activeLang.ui.img_alt_ifip} style={{ height: '50px', marginBottom: '20px' }} />
          <h2 id="mobile-sef-blocker-title" style={{ fontSize: '1.5rem', marginBottom: '15px', fontWeight: '800' }}>{activeLang.ui.mobile_blocker_title}</h2>
          <p style={{ opacity: 0.85, lineHeight: '1.6', fontSize: '0.9rem', maxWidth: '400px', margin: '0 auto' }}>
            {activeLang.ui.mobile_blocker_body}
          </p>
          <button
            type="button"
            className="mobile-only-blocker__dashboard-btn"
            onClick={() => setPortalView('DASHBOARD')}
          >
            <span aria-hidden="true">←</span>{' '}
            {activeLang.ui.btn_back_dashboard || APP_UI_EN.btn_back_dashboard}
          </button>
        </div>
      </div>
      )}
      {sefChoicePending && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="sef-choice-title"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 9000,
            background: 'rgba(8, 16, 32, 0.72)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 24,
          }}
        >
          <div style={{
            maxWidth: 560,
            width: '100%',
            background: 'var(--a11y-surface, #102033)',
            color: 'var(--a11y-page-text, #fff)',
            borderRadius: 16,
            padding: 28,
            border: '1px solid rgba(255,255,255,0.15)',
          }}>
            <h2 id="sef-choice-title" style={{ marginTop: 0 }}>{activeLang.ui.sef_choice_title || enMaster.ui.sef_choice_title}</h2>
            <p style={{ lineHeight: 1.5 }}>{activeLang.ui.sef_choice_body || enMaster.ui.sef_choice_body}</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <button
                type="button"
                className="btn-primary-action"
                disabled={sefChoiceBusy}
                onClick={() => applySefCarryChoice('retain')}
              >
                {activeLang.ui.sef_choice_keep || enMaster.ui.sef_choice_keep}
              </button>
              <p style={{ margin: 0, opacity: 0.8 }}>{activeLang.ui.sef_choice_keep_hint || enMaster.ui.sef_choice_keep_hint}</p>
              <button
                type="button"
                disabled={sefChoiceBusy}
                onClick={() => applySefCarryChoice('blank')}
                style={{ padding: '12px 16px', borderRadius: 8, cursor: 'pointer' }}
              >
                {activeLang.ui.sef_choice_blank || enMaster.ui.sef_choice_blank}
              </button>
              <p style={{ margin: 0, opacity: 0.8 }}>{activeLang.ui.sef_choice_blank_hint || enMaster.ui.sef_choice_blank_hint}</p>
            </div>
          </div>
        </div>
      )}
     <header className="portal-header">
  <div className="portal-header-inner">
    <div className="portal-header-brand-cluster">
      {(portalView !== 'DASHBOARD' || showQuestionnaire) && (
        <button 
          className="btn-back-dashboard portal-header-action-btn" 
          style={{ whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: '8px' }}
          onClick={() => {
            setPortalView('DASHBOARD');
            setIsReviewMode(false);
            setShowQuestionnaire(false);
          }}
        >
          <span style={{ flexShrink: 0 }}>←</span> {activeLang.ui.btn_back_dashboard || "Dashboard"}
        </button>
      )}
      <img className="portal-header-logo" src="/images/01%20IFIP%20Logo.png" alt="IFIP Logo" />
      <PortalSchoolLogo
        schoolId={schoolId}
        portalLogo={schoolProfile?.portalLogo}
        labels={activeLang.ui}
        onLogoChange={(next) => setSchoolProfile((prev) => ({ ...(prev || {}), portalLogo: next || undefined }))}
        onSyncStatus={setSyncStatus}
      />
      <div className="portal-header-branding">
        <h1 className="portal-header-title">{activeLang.ui.portal_title}</h1>
        {schoolName ? (
          <p className="portal-header-school">{schoolName}</p>
        ) : null}
        <p className="portal-header-subtitle">{activeLang.ui.portal_subtitle}</p>
      </div>
    </div>

    {/* --- RIGHT: TOOLS & STATUS --- */}
    <div className="portal-header-tools">
      <div className="portal-header-row portal-header-row--primary">
        <div
          className={`portal-header-meta-pill sync-tone-${getSyncIndicatorTone(syncStatus)}`}
        >
          {activeLang.ui.sync_indicator_prefix} {syncStatus}
        </div>

        <span className="portal-header-meta-pill portal-header-meta-pill--status portal-header-control-text">
          {activeLang.ui.status_label}{' '}
          {schoolProfile?.workflowStep === 'finalised'
            ? (activeLang.ui.status_cycle_complete || 'Cycle complete')
            : (auditStatus === 'Submitted' ? activeLang.ui.status_submitted : activeLang.ui.status_draft)}
        </span>
      </div>

      <div className="portal-header-row portal-header-row--secondary">
        {isGlobalTranslating && (
          <span
            className={`portal-header-meta-pill portal-header-meta-pill--translating portal-header-control-text ${langKey === 'en' ? 'translating-dots' : 'translating-dots translating-dots--plain'}`}
          >
            {getTranslatingIndicator(langKey)}
          </span>
        )}

        <div
          className="portal-header-actions-panel"
          role="toolbar"
          aria-label={activeLang.ui.portal_header_action_bar_aria || 'Accessibility, language, and translation support'}
        >
          <div className="portal-header-actions-panel__segment">
            <div className="portal-header__a11y-slot">
              <AccessibilityControls
                panelOpen={a11yPanelOpen}
                setPanelOpen={setA11yPanelOpen}
                scheme={a11yScheme}
                setScheme={setA11yScheme}
                labels={getA11yLabels()}
                rootClassName="ifip-a11y-control-root ifip-a11y-control-root--header"
                panelId="a11y-panel-app"
                showPortalHoverZoomToggle={!isNarrowViewport}
                hoverZoomOthers={hoverZoomOthers}
                onHoverZoomOthersChange={setHoverZoomOthers}
              />
            </div>
          </div>

          <div className="portal-header-actions-panel__segment">
            <div className="portal-header-lang-inline">
              <PortalHeaderGlobeIcon className="portal-header-panel-icon" />
              <label htmlFor="portal-header-lang-select" className="portal-header-sr-only">
                {activeLang.ui.grid_labels?.language || activeLang.ui.nav_lang_select || 'Language'}
              </label>
              <select
                id="portal-header-lang-select"
                className="portal-header-lang-select portal-header-lang-select--in-panel"
                value={langKey}
                onChange={(e) => setLangKey(e.target.value)}
                aria-label={activeLang.ui.nav_lang_select || activeLang.ui.grid_labels?.language || 'Language'}
              >
                {Object.entries(languageManifest)
                  .sort((a, b) => (a[1].nativeName || a[1].label || a[0]).localeCompare(b[1].nativeName || b[1].label || b[0]))
                  .map(([key, lang]) => (
                  <option key={key} value={key}>{lang.nativeName}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="portal-header-actions-panel__segment portal-header-actions-panel__segment--report-mail">
            <a
              href={`mailto:support@ifip.group?subject=Translation Concern: ${schoolId}`}
              className="portal-header-report-translation"
            >
              <PortalHeaderFlagIcon className="portal-header-panel-icon" />
              <span>{activeLang.ui.btn_report_translation}</span>
            </a>
          </div>
          <div className="portal-header-actions-panel__segment">
            <SupportHelpButton
              label={activeLang.ui.help_btn || APP_UI_EN.help_btn}
              onClick={() => setShowSupportModal(true)}
              style={{ minHeight: 34 }}
            />
          </div>
        </div>

        {isReviewMode && (
          <button
            type="button"
            className="portal-header-meta-pill portal-header-meta-pill--accent"
            onClick={() => setIsReviewMode(false)}
          >
            {activeLang.ui.btn_back_edit}
          </button>
        )}
      </div>
    </div>
  </div>
</header>

      {showQuestionnaire ? (
        <Questionnaire
          schoolId={schoolId}
          langKey={langKey}
          activeLang={activeLang}
          schoolProfile={schoolProfile}
          onComplete={() => setShowQuestionnaire(false)}
        />
      ) : portalView === 'DASHBOARD' ? (
        <Dashboard />
       ) : portalView === 'REPORTS' ? (
        <AccreditationHub
          schoolId={schoolId}
          schoolProfile={schoolProfile}
          activeLang={activeLang}
          langKey={langKey}
          registerTranslating={registerTranslating}
          onSchoolProfilePatch={(patch) =>
            setSchoolProfile((prev) => ({ ...(prev || {}), ...patch }))
          }
        />
      ) : isReviewMode ? (
        <main className="review-container" style={{ padding: '40px', maxWidth: '1100px', margin: '0 auto' }}>
          
          <section style={{ 
            background: '#0a1221', // Deep dark navy for maximum contrast
            border: '2px solid #138a8a', 
            borderRadius: '16px', 
            padding: '40px', 
            marginBottom: '50px',
            boxShadow: '0 25px 60px rgba(0,0,0,0.6)',
            position: 'relative'
          }}>
            {/* Design Element */}
            <div style={{ position: 'absolute', top: '-2px', left: '20px', right: '20px', height: '4px', background: 'linear-gradient(90deg, #138a8a, #d11d7e)', borderRadius: '2px' }}></div>
            
            <h2 style={{ marginTop: 0, color: '#fff', fontSize: '1.75rem' }}>
  {activeLang.ui.diagnostic_title}
</h2>

<p style={{ color: '#138a8a', fontWeight: 'bold', marginBottom: '20px', textTransform: 'uppercase', fontSize: '0.8rem', letterSpacing: '1px' }}>
  {activeLang.ui.diagnostic_intro}
</p>

<ul style={{ paddingLeft: '0', listStyle: 'none' }}>
  {isAiAnalyzing ? (
    <li style={{ color: '#fbbf24', fontStyle: 'italic', animation: 'pulse 2s infinite' }}>
       {activeLang.ui.ai_analyzing_submission}
    </li>
  ) : (
    <>
      {/* 1. WE HARD-CODE THE LONG DISCLAIMER HERE AS THE FIRST ITEM */}
      <li style={{ 
        marginBottom: '20px', fontSize: '0.95rem', color: '#fbbf24', 
        padding: '15px 20px', background: 'rgba(251, 191, 36, 0.1)', 
        borderRadius: '8px', borderLeft: '4px solid #fbbf24', lineHeight: '1.5'
      }}>
        <strong>NOTICE:</strong> {activeLang.ui.ai_disclaimer_long}
      </li>

      {/* 2. THEN SHOW THE ACTUAL AI FEEDBACK TIPS */}
      {diagnosticFeedback.map((tip, i) => (
        <li key={i} style={{ 
          marginBottom: '15px', fontSize: '1.05rem', color: '#fff', 
          lineHeight: '1.6', padding: '12px 20px', 
          background: 'rgba(255,255,255,0.03)', borderRadius: '8px', 
          borderLeft: '4px solid #138a8a'
        }}>
          {formatAiText(tip)}
        </li>
      ))}
    </>
  )}
</ul>
          </section>

          <h2>{activeLang.ui.review_header}</h2>
          {stations.map(s => {
            // Fix: Point to auditData.stations
            const data = auditData.stations[s.id] || { selectedLevel: 1, narrative: "" };
            // Fix: Filter from evidenceVault
            const linkedFiles = auditData.evidenceVault?.filter(f => f.tags?.includes(s.id)) || [];

            return (
              <div key={s.id} style={{ background: 'rgba(255,255,255,0.05)', padding: '20px', borderRadius: '8px', marginBottom: '20px', border: '1px solid rgba(255,255,255,0.1)' }}>
                <h3 style={{ color: '#138a8a' }}>
                  {s.title} — {activeLang.ui.review_station_title_suffix} {data.selectedLevel}
                </h3>
                
                <p style={{ whiteSpace: 'pre-wrap', fontSize: '0.9rem', margin: '15px 0', opacity: 0.9 }}>
                  {data.narrative || activeLang.ui.review_no_narrative}
                </p>
                
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '10px', borderTop: '1px solid rgba(255,255,255,0.05)', paddingTop: '10px' }}>
  {linkedFiles.map((f, idx) => (
    <span key={idx} style={{ 
      fontSize: '0.65rem', padding: '4px 10px', 
      background: 'rgba(19, 138, 138, 0.1)', 
      border: '1px solid rgba(19, 138, 138, 0.3)', 
      borderRadius: '4px', color: '#138a8a', display: 'flex', alignItems: 'center', gap: '6px' 
    }}>
      📄 {f.name}
    </span>
  ))}
  
  {linkedFiles.length === 0 && (
    <span style={{ fontSize: '0.7rem', opacity: 0.4, fontStyle: 'italic' }}>
      (---)
    </span>
  )}
</div>
              </div>
            );
          })}

          <div style={{ textAlign: 'center', marginTop: '40px' }}>
             <button onClick={submitFinalAudit} style={{ background: '#d11d7e', color: 'white', padding: '15px 30px', fontWeight: 'bold', borderRadius: '8px', border: 'none', cursor: 'pointer' }}>{activeLang.ui.btn_confirm_final}</button>
	<p style={{ 
  marginTop: '20px', 
  fontSize: '0.85rem', 
  opacity: 0.7, 
  color: '#fff', 
  maxWidth: '500px', 
  margin: '20px auto 0',
  lineHeight: '1.5' 
}}>
  {activeLang.ui.submit_warning}
</p>
          </div>
        </main>
   
  ) : (
        <main className="two-column-workspace">
          
         {/* --- COLUMN 2: NARRATIVE --- */}
<section className="column-narrative">
<div className="audit-frame" style={{ height: '100%', boxSizing: 'border-box' }}>  
{/* STEP 1: STATION SELECTOR (MOVED HERE) */}
  <div style={{ marginBottom: '30px', borderBottom: '1px solid rgba(255,255,255,0.05)', paddingBottom: '20px' }}>
    <label className="sef-step-badge">
      {activeLang.ui.workflow_step_1}
    </label>
	<p className="sef-step-hint sef-hover-zoom-target sef-hover-zoom-target--wide" style={{ margin: '0 0 25px 0' }}>
  {activeLang.accreditationForm.reg_ui.auditInstructions}
</p>

    <nav id="station-nav-guide" className="station-nav" style={{ marginTop: 0, display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
      {stations.map((s, i) => (
        <button
          key={s.id}
          type="button"
          onClick={() => setActiveStationIndex(i)}
          className={`sef-hover-zoom-target ${i === activeStationIndex ? 'active' : ''}`}
        >
          {activeLang.ui.nav_station_prefix} {s.id} {auditData.stations[s.id]?.narrative?.length > 100 && "✓"}
        </button>
      ))}
    </nav>
  </div>
</div>  

  {/* STEP 2: MATURITY SELECTOR */}
<div className="audit-frame" style={{ height: '100%', boxSizing: 'border-box' }}>  
<div id="matrix-grid-guide" style={{ marginBottom: '40px' }}>
    <label className="sef-step-badge" style={{ marginBottom: '20px' }}>
      {activeLang.ui.workflow_step_2}
    </label>
    <div className="sef-hover-zoom-target sef-hover-zoom-target--wide sef-step2-intro">
    <p className="sef-step-hint" style={{ margin: '0 0 25px 0' }}>

      {activeLang.ui.workflow_step_2_instruction}
    </p>

    <h2 className="sef-station-title">
      {currentStation.title}
    </h2>
    <p className="sef-station-def">
      {currentStation.shortDefinition}
    </p>
    </div>
    
    <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
      {[1, 2, 3, 4, 5].map(num => {
        const gridLevel = currentStation.diagnosticGrid[num];
        const tipOpen = isLevelTipVisible(num);
        return (
          <div
            key={num}
            className="level-btn-wrapper"
            style={{ flex: 1, position: 'relative' }}
            onMouseEnter={() => {
              if (pinnedLevelTip === null) setHoveredLevelTip(num);
            }}
            onMouseLeave={() => {
              if (pinnedLevelTip === null) setHoveredLevelTip(null);
            }}
          >
            <button 
              type="button"
              onClick={() => handleUpdate({ selectedLevel: num })} 
              className={`level-btn sef-hover-zoom-target ${stationState.selectedLevel === num ? 'selected' : ''}`}
              style={{ 
                width: '100%', padding: '15px 5px', borderRadius: '8px', cursor: 'pointer', 
                border: '1px solid rgba(255,255,255,0.2)', 
                background: stationState.selectedLevel === num ? '#138a8a' : 'rgba(0,0,0,0.2)', 
                color: 'white', fontWeight: 'bold' 
              }}
            >
              Lvl {num}
            </button>

            <LevelMaturityTooltip
              num={num}
              gridLevel={gridLevel}
              gridLabels={activeLang.ui.grid_labels}
              tipOpen={tipOpen}
              isPinned={pinnedLevelTip === num}
              pinLabel={activeLang.ui.level_tooltip_pin}
              unpinLabel={activeLang.ui.level_tooltip_unpin}
              pinHint={activeLang.ui.level_tooltip_pin_hint}
              onHoverEnter={() => {
                if (pinnedLevelTip === null) setHoveredLevelTip(num);
              }}
              onPin={(level) => {
                setPinnedLevelTip(level);
                setHoveredLevelTip(level);
              }}
              onClose={() => {
                setPinnedLevelTip(null);
                setHoveredLevelTip(null);
              }}
            />
          </div>
        );
      })}
    </div>
</div>
  {/* STEP 3: NARRATIVE INPUT */}
  
<div id="narrative-input-guide" className="narrative-input sef-hover-zoom-target sef-hover-zoom-target--wide" style={{ marginTop: '40px' }}>
    <label className="sef-step-badge">
      {activeLang.ui.workflow_step_3}
    </label>
	<p className="sef-step-hint" style={{ margin: '0 0 25px 0' }}>

      {activeLang.ui.workflow_step_3_instruction}
    </p>

    <textarea 
      value={stationState.narrative} 
      onChange={(e) => handleUpdate({ narrative: e.target.value })} 
      placeholder={activeLang.ui.placeholder_narrative} 
      disabled={sefReadOnly} 
    />
  </div>
</div>
</section>

          {/* --- COLUMN 2: EVIDENCE (RESTORED RIGHT) --- */}

	<section id="evidence-section-guide" className="column-evidence">
	<div className="audit-frame">
 	{/* STEP 4 WORKFLOW HEADER */}
  <label className="sef-step-badge">
    {activeLang.ui.workflow_step_4}
  </label>
  <div className="sef-evidence-vault-intro sef-hover-zoom-target sef-hover-zoom-target--wide">
  <h2>{activeLang.accreditationForm.reg_ui.header_evidenceVault}</h2>
  
  <div className="upload-zone" style={{ textAlign: 'center', padding: '20px', border: '2px dashed rgba(255,255,255,0.1)', borderRadius: '12px' }}>
  <p style={{ marginBottom: '15px', fontSize: '0.9rem' }}>{activeLang.ui.dropzone_instruction}</p>
  
  <button 
    disabled={sefReadOnly} 
    onClick={() => document.getElementById('file-upload').click()} 
    className="browse-btn"
    style={{ 
      background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.4)', 
      color: 'white', padding: '10px 20px', borderRadius: '8px', cursor: 'pointer', 
      fontWeight: 'bold', marginBottom: '15px' 
    }}
  >
    {activeLang.ui.btn_click_browse}
  </button>

  {/* REINSTATED HINT BLOCK */}
  <span style={{ 
    display: 'block', fontSize: '0.75rem', opacity: 0.6, 
    lineHeight: '1.4', padding: '0 10px', fontStyle: 'italic' 
  }}>
    {activeLang.ui.evidence_hint}
  </span>

  <input id="file-upload" type="file" style={{ display: 'none' }} onChange={handleFileUpload} />
</div>
  </div>

  <div className="evidence-list" style={{ marginTop: '20px' }}>
    <h3 style={{ fontSize: '0.9rem', color: '#138a8a', marginBottom: '15px', textTransform: 'uppercase' }}>
      {activeLang.ui.header_accepted_evidence} ({auditData.evidenceVault?.length || 0})
    </h3>
    
  {auditData.evidenceVault?.map((file, index) => {
  const isTaggedToCurrent = file.tags?.includes(currentStation.id);
  const extension = file.name.split('.').pop().toLowerCase();
  const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(extension);
  
  const badgeStyle = (bg) => ({
    background: bg, color: 'white', fontSize: '0.55rem', fontWeight: '900',
    padding: '3px 0', borderRadius: '3px', width: '28px', textAlign: 'center',
    lineHeight: '1', textTransform: 'uppercase'
  });

  const fileIcon = isImage 
    ? <img src={file.url} alt="thumb" style={{ width: '28px', height: '28px', objectFit: 'cover', borderRadius: '4px' }} />
    : extension === 'pdf' ? <div style={badgeStyle('#ef4444')}>PDF</div>
    : ['doc', 'docx'].includes(extension) ? <div style={badgeStyle('#3b82f6')}>DOC</div>
    : ['xls', 'xlsx', 'csv'].includes(extension) ? <div style={badgeStyle('#22c55e')}>XLS</div>
    : <div style={badgeStyle('#64748b')}>{extension.slice(0,3)}</div>;

  
  return (
            <div
              key={index}
              className="sef-evidence-item sef-hover-zoom-target sef-hover-zoom-target--wide"
              style={{ background: isTaggedToCurrent ? 'rgba(19, 138, 138, 0.12)' : 'rgba(255,255,255,0.03)', padding: '12px', borderRadius: '8px', marginBottom: '10px', border: isTaggedToCurrent ? '1px solid #138a8a' : '1px solid rgba(255,255,255,0.1)' }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{ fontSize: '1.2rem', display: 'flex', alignItems: 'center', justifyContent: 'center', width: '24px' }}>{fileIcon}</span>
                <div style={{ flex: 1, overflow: 'hidden' }}>
                  <p style={{ fontSize: '0.75rem', margin: 0, fontWeight: '600', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{file.name}</p>
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <a href={file.url} target="_blank" rel="noreferrer" style={{ textDecoration: 'none', fontSize: '0.8rem' }}>🔍</a>
                  {!sefReadOnly && (
                    <button onClick={() => handleFileDelete(file.name)} style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', fontSize: '0.8rem' }}>❌</button>
                  )}
                </div>
              </div>
              {/* TAGGER BAR */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginTop: '12px', background: 'rgba(0,0,0,0.2)', padding: '4px', borderRadius: '4px' }}>
                <span style={{ fontSize: '0.55rem', opacity: 0.5, marginRight: '4px' }}>TAG:</span>
                {stations.map(s => (
                  <button 
                    key={s.id} 
                    onClick={() => updateFileTags(file.name, s.id)} 
                    style={{ width: '22px', height: '22px', fontSize: '0.6rem', borderRadius: '4px', border: 'none', background: file.tags?.includes(s.id) ? '#138a8a' : 'rgba(255,255,255,0.1)', color: '#fff', cursor: 'pointer', fontWeight: 'bold' }}
                  >
                    {s.id}
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>
   </div>

      {/* --- STEP 5: REVIEW & SUBMIT (NEW) --- */}
	<div className="audit-frame" style={{ flex: 1 }}> {/* flex: 1 makes this grow to fill height if needed */}
      <div id="review-btn-guide" className="sef-hover-zoom-target sef-hover-zoom-target--wide sef-step5-review">
        <label className="sef-step-badge">
          {activeLang.ui.workflow_step_5}
        </label>
        
        <p style={{ fontSize: '0.85rem', opacity: 0.8, lineHeight: '1.5', marginBottom: '20px' }}>
          {activeLang.ui.workflow_step_5_instruction}
        </p>

        <button 
          type="button"
          onClick={() => setIsReviewMode(true)}
          disabled={sefReadOnly}
          style={{ 
            width: '100%',
            padding: '15px', 
            borderRadius: '8px', 
            cursor: 'pointer', 
            border: 'none', 
            background: '#d11d7e', 
            color: 'white', 
            fontWeight: 'bold',
            fontSize: '1rem',
            transition: 'all 0.2s'
          }}
          onMouseOver={(e) => !e.target.disabled && (e.target.style.opacity = '0.9')}
          onMouseOut={(e) => e.target.style.opacity = '1'}
        >
          {activeLang.ui.btn_start_review || "Begin Final Review"}
        </button>
      </div>
     </div>
    </section>
  </main>
      )}

      {!isReviewMode && (
        <>
          {/* FLOATING BUTTON 1: HANDBOOK (ONLY IN SEF) */}
          {portalView === 'SEF' && (
            <button type="button" id="handbook-guide" className="portal-handbook-tab-btn"
              onClick={() => setOpenPanel(openPanel === 'guidance' ? null : 'guidance')}
              style={{
                position: 'fixed',
                top: '96px',
                [isRTL ? 'left' : 'right']: 'var(--portal-drawer-edge-shift, 0px)',
                zIndex: 8511,
                transform: isRTL ? 'rotate(90deg)' : 'rotate(-90deg)', transformOrigin: isRTL ? 'left bottom' : 'right bottom',
                padding: '10px 22px', cursor: 'pointer',
                whiteSpace: 'nowrap', borderRadius: '8px 8px 0 0', transition: 'all 0.3s ease-in-out'
              }}
            >
              {openPanel === 'guidance' ? `→ ${activeLang.ui.tab_guidance_close}` : `← ${activeLang.ui.tab_guidance_open}`}
            </button>
          )}

          {/* FLOATING BUTTON 2: PROFILE (ALWAYS) */}
          <button 
            type="button"
            className="portal-profile-tab-btn"
            onClick={() => setOpenPanel(openPanel === 'profile' ? null : 'profile')}
            style={{
              position: 'fixed',
              top: portalView === 'SEF' ? '340px' : '132px',
              [isRTL ? 'left' : 'right']: 'var(--portal-drawer-edge-shift, 0px)',
              zIndex: 8510,
              transform: isRTL ? 'rotate(90deg)' : 'rotate(-90deg)', transformOrigin: isRTL ? 'left bottom' : 'right bottom',
              padding: '10px 22px', cursor: 'pointer',
              whiteSpace: 'nowrap', borderRadius: '8px 8px 0 0', transition: 'all 0.3s ease-in-out'
            }}
          >
            {openPanel === 'profile' ? `→ ${activeLang.ui.tab_profile_close}` : `← ${activeLang.ui.tab_profile_open}`}
          </button>

          {/* THE SINGLE SLIDING DRAWER */}
          <aside 
            className="portal-profile-drawer"
            style={{
              position: 'fixed', top: 0, bottom: 0,
              [isRTL ? 'left' : 'right']: openPanel ? '0px' : portalDrawerOffscreenEdgeCss(),
              width: portalDrawerWidthCss(),
              transition: 'all 0.3s ease-in-out',
              boxShadow: isRTL ? '10px 0 30px rgba(0,0,0,0.35)' : '-10px 0 30px rgba(0,0,0,0.35)',
              textAlign: isRTL ? 'right' : 'left', display: 'flex', flexDirection: 'column'
            }}
          >
            {openPanel ? (
              <div className="portal-profile-drawer__mobile-bar">
                <button
                  type="button"
                  className="portal-profile-drawer__mobile-close"
                  onClick={() => setOpenPanel(null)}
                >
                  {openPanel === 'profile'
                    ? (activeLang.ui.tab_profile_close || 'Close Profile')
                    : (activeLang.ui.tab_guidance_close || 'Close Guidance')}
                </button>
              </div>
            ) : null}
            <div className="guidance-content guidance-content--portal-drawer" style={{ overflowY: 'auto', flex: 1 }}>
              {openPanel === 'guidance' && (
                <>
                  <h3 className="portal-drawer-heading">{activeLang.ui.header_station_guidance} {currentStation.title}</h3>
                  <p className="portal-drawer-lead">{currentStation.fullDescription}</p>
                  <h4 className="portal-drawer-subheading">{activeLang.ui.header_assessment_matrix}</h4>
                  <ul style={{ listStyle: 'none', padding: 0 }}>
                    {currentStation.assessmentMatrix.map(item => (
                      <li key={item.id} className="portal-drawer-matrix-item">
                        <strong>{item.label}:</strong> 
                        <div className="portal-drawer-matrix-desc">{item.description}</div>
                      </li>
                    ))}
                  </ul>
                </>
              )}

              {openPanel === 'profile' && (
                <div className="snapshot-drawer-inner">
                  <h2 className="snapshot-drawer-title">
                    {activeLang.accreditationForm.reg_ui.snapshot_header}
                  </h2>
                  <p className="snapshot-drawer-intro">
                    {activeLang.accreditationForm.reg_ui.snapshot_intro}
                  </p>

                  {!schoolProfile ? (
                    <p className="snapshot-drawer-muted">{activeLang.accreditationForm.reg_ui.snapshot_no_data}</p>
                  ) : (
                    <>
                      {/* 1. Stage (Read Only) */}
                      <div className="snapshot-item">
                        <span className="snapshot-field-label">
                          {activeLang.accreditationForm.reg_ui.snapshot_label_stage}
                        </span>
                        <span className="snapshot-field-value">
                          {activeLang.accreditationForm.stages[schoolProfile.accreditationLevel]?.title || schoolProfile.accreditationLevel || "---"}
                        </span>
                      </div>

                      {/* 2. Lead Contact (Editable) */}
                      <div className="snapshot-item">
                        <span className="snapshot-field-label">
                          {activeLang.accreditationForm.reg_ui.snapshot_label_contact}
                        </span>
                        {editingField === 'orgLead' ? (
                          <div>
                            <input className="snapshot-edit-input" type="text" value={editValue} onChange={(e) => setEditValue(e.target.value)} autoFocus onKeyDown={(e) => { if (e.key === 'Enter') handleSchoolProfileUpdate('orgLead', editValue); if (e.key === 'Escape') setEditingField(null); }} />
                            <div className="snapshot-drawer-btn-row">
                              <button type="button" className="snapshot-drawer-btn-primary" onClick={() => handleSchoolProfileUpdate('orgLead', editValue)}>✓</button>
                              <button type="button" className="snapshot-drawer-btn-ghost" onClick={() => setEditingField(null)}>✕</button>
                            </div>
                          </div>
                        ) : (
                          <div className="snapshot-drawer-row">
                            <span className="snapshot-field-value">{schoolProfile?.answers?.orgLead || "---"}</span>
                            <button type="button" className="snapshot-drawer-icon-btn" onClick={() => { setEditingField('orgLead'); setEditValue(schoolProfile?.answers?.orgLead || ""); }}>✎</button>
                          </div>
                        )}
                      </div>

                      {/* 3. Students on Roll (Editable) */}
                      <div className="snapshot-item">
                        <span className="snapshot-field-label">
                          {activeLang.accreditationForm.reg_ui.snapshot_label_students}
                        </span>
                        {editingField === 'q7' ? (
                          <div>
                            <input className="snapshot-edit-input" type="number" value={editValue} onChange={(e) => setEditValue(e.target.value)} autoFocus onKeyDown={(e) => { if (e.key === 'Enter') handleSchoolProfileUpdate('q7', editValue); if (e.key === 'Escape') setEditingField(null); }} />
                            <div className="snapshot-drawer-btn-row">
                              <button type="button" className="snapshot-drawer-btn-primary" onClick={() => handleSchoolProfileUpdate('q7', editValue)}>✓</button>
                              <button type="button" className="snapshot-drawer-btn-ghost" onClick={() => setEditingField(null)}>✕</button>
                            </div>
                          </div>
                        ) : (
                          <div className="snapshot-drawer-row">
                            <span className="snapshot-field-value">{schoolProfile?.answers?.q7 || "---"}</span>
                            <button type="button" className="snapshot-drawer-icon-btn" onClick={() => { setEditingField('q7'); setEditValue(schoolProfile?.answers?.q7 || ""); }}>✎</button>
                          </div>
                        )}
                      </div>

                      {/* 4. Student Need Profile (Mapping) */}
                      <div className="snapshot-item">
                        <span className="snapshot-field-label snapshot-field-label--spaced">
                          {activeLang.accreditationForm.reg_ui.snapshot_label_needs}
                        </span>
                        {editingField === 'q10' ? (
                          <div className="snapshot-embed-panel">
                            {(findRegistrationQuestion(activeLang.accreditationForm, 'q10')?.categories || []).map(cat => (
                              <div key={cat} className="snapshot-drawer-cat-row">
                                <span>{cat}</span>
                                <input type="number" value={editValue?.[cat] || 0} onChange={(e) => setEditValue({ ...editValue, [cat]: e.target.value })} />
                              </div>
                            ))}
                            <div className="snapshot-drawer-btn-row" style={{ marginTop: '10px' }}>
                              <button type="button" className="snapshot-drawer-btn-primary" onClick={() => handleSchoolProfileUpdate('q10', editValue)}>✓</button>
                              <button type="button" className="snapshot-drawer-btn-ghost" onClick={() => setEditingField(null)}>✕</button>
                            </div>
                          </div>
                        ) : (
                          <div className="snapshot-drawer-row snapshot-drawer-row--top">
                            <div className="snapshot-drawer-tag-wrap">
                              {(findRegistrationQuestion(activeLang.accreditationForm, 'q10')?.categories || []).map(cat => (
                                <span key={cat} className="snapshot-chip">
                                  {cat}: {schoolProfile?.answers?.q10?.[cat] || 0}%
                                </span>
                              ))}
                            </div>
                            <button type="button" className="snapshot-drawer-icon-btn" onClick={() => { setEditingField('q10'); setEditValue(schoolProfile?.answers?.q10 || {}); }}>✎</button>
                          </div>
                        )}
                      </div>
			
			{/* 4. Total Staff (Editable) */}
<div className="snapshot-item">
  <span className="snapshot-field-label">
    {activeLang.accreditationForm.reg_ui.snapshot_label_total_staff}
  </span>
  {editingField === 'q12' ? (
    <div>
      <input 
        className="snapshot-edit-input" 
        type="number" 
        value={editValue} 
        onChange={(e) => setEditValue(e.target.value)} 
        autoFocus 
      />
      <div className="snapshot-drawer-btn-row">
        <button type="button" className="snapshot-drawer-btn-primary" onClick={() => handleSchoolProfileUpdate('q12', editValue)}>✓</button>
        <button type="button" className="snapshot-drawer-btn-ghost" onClick={() => setEditingField(null)}>✕</button>
      </div>
    </div>
  ) : (
    <div className="snapshot-drawer-row">
      <span className="snapshot-field-value">{schoolProfile?.answers?.q12 || "0"}</span>
      <button type="button" className="snapshot-drawer-icon-btn" onClick={() => { setEditingField('q12'); setEditValue(schoolProfile?.answers?.q12 || ""); }}>✎</button>
    </div>
  )}
</div>
                      {/* 5. Specialist Staff (Editable) */}
                      <div className="snapshot-item">
                        <span className="snapshot-field-label">
                          {activeLang.accreditationForm.reg_ui.snapshot_label_specialists}
                        </span>
                        {editingField === 'specialistTotal' ? (
                          <div>
                            <input className="snapshot-edit-input" type="number" value={editValue} onChange={(e) => setEditValue(e.target.value)} autoFocus />
                            <div className="snapshot-drawer-btn-row">
                              <button type="button" className="snapshot-drawer-btn-primary" onClick={() => handleSchoolProfileUpdate('q13.total', editValue)}>✓</button>
                              <button type="button" className="snapshot-drawer-btn-ghost" onClick={() => setEditingField(null)}>✕</button>
                            </div>
                          </div>
                        ) : (
                          <div className="snapshot-drawer-row">
                            <span className="snapshot-field-value">{activeLang.accreditationForm.reg_ui.snapshot_headcount_suffix} - {schoolProfile?.answers?.q13?.total || 0}</span>
                            <button type="button" className="snapshot-drawer-icon-btn" onClick={() => { setEditingField('specialistTotal'); setEditValue(schoolProfile?.answers?.q13?.total || 0); }}>✎</button>
                          </div>
                        )}
                      </div>
			{/* 6. Recent Training (Editable Checklist) */}
<div className="snapshot-item">
  <span className="snapshot-field-label snapshot-field-label--spaced">
    {activeLang.accreditationForm.reg_ui.snapshot_label_training}
  </span>
  
  {editingField === 'q15' ? (
    <div className="snapshot-embed-panel">
      {(findRegistrationQuestion(activeLang.accreditationForm, 'q15')?.options || []).map(opt => (
        <label key={opt} className="snapshot-q15-label">
          <input 
            type="checkbox" 
            checked={editValue?.includes(opt) || false}
            onChange={(e) => {
              const current = editValue || [];
              setEditValue(e.target.checked ? [...current, opt] : current.filter(v => v !== opt));
            }}
          />
          <span>{opt}</span>
        </label>
      ))}
      <div className="snapshot-drawer-btn-row" style={{ marginTop: '10px' }}>
        <button type="button" className="snapshot-drawer-btn-primary" onClick={() => handleSchoolProfileUpdate('q15', editValue)}>✓</button>
        <button type="button" className="snapshot-drawer-btn-ghost" onClick={() => setEditingField(null)}>✕</button>
      </div>
    </div>
  ) : (
    <div className="snapshot-drawer-row snapshot-drawer-row--top">
      <div className="snapshot-drawer-tag-wrap">
        {schoolProfile?.answers?.q15?.length > 0 ? (
          schoolProfile.answers.q15.map(item => (
            <span key={item} className="snapshot-chip snapshot-chip--accent">{item}</span>
          ))
        ) : (
          <span className="snapshot-drawer-muted" style={{ fontSize: '0.75rem' }}>{activeLang.ui.label_none_recorded}</span>
        )}
      </div>
      <button type="button" className="snapshot-drawer-icon-btn" onClick={() => { setEditingField('q15'); setEditValue(schoolProfile?.answers?.q15 || []); }}>✎</button>
    </div>
  )}
</div>
                    </>
                  )}
                </div>
              )}
            </div>
          </aside>
        </>
      )}

  <GuidedTour 
  isOpen={isTourOpen && !isNarrowViewport} 
  langKey={langKey} 
  activeLang={activeLang} 
  onClose={() => {
    setIsTourOpen(false);
    localStorage.setItem('ifip_tour_complete', 'true');
  }}
  setOpenPanel={setOpenPanel} 
/>

      {!isTourOpen && !isNarrowViewport && hasEntered && portalView === 'SEF' && !isReviewMode && !showQuestionnaire && (
        <button 
          onClick={() => setIsTourOpen(true)}
          style={{
            position: 'fixed', bottom: '30px', right: '30px',
            width: '50px', height: '50px', borderRadius: '50%',
            background: '#138a8a', color: 'white', border: 'none',
            fontSize: '24px', cursor: 'pointer', zIndex: 9999,
            boxShadow: '0 4px 15px rgba(0,0,0,0.3)'
          }}
        >
          ?
        </button>
      )}

    {showSefSuccess && (
        <div className="modal-overlay" style={{ zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(10, 18, 33, 0.9)', backdropFilter: 'blur(5px)' }}>
          <div className="success-modal" style={{ background: '#1a2a44', border: '2px solid #138a8a', padding: '40px', borderRadius: '16px', maxWidth: '500px', textAlign: 'center', boxShadow: '0 25px 50px rgba(0,0,0,0.5)', animation: 'scaleUp 0.3s ease-out' }}>
            <div style={{ fontSize: '4rem', marginBottom: '20px' }}>🏆</div>
            <h2 style={{ color: '#fff', marginBottom: '15px' }}>{activeLang.accreditationForm.reg_ui.success_sef}</h2>
            <button 
              onClick={() => setShowSefSuccess(false)}
              style={{ background: '#138a8a', color: 'white', border: 'none', padding: '12px 30px', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer', fontSize: '1rem' }}
            >
              {activeLang.ui.btn_back_dashboard}
            </button>
          </div>
        </div>
      )}
    <SupportIssueModal
      isOpen={showSupportModal}
      onClose={() => setShowSupportModal(false)}
      sourceSurface="school_portal"
      context={supportContext}
    />
    </div>
    </Fragment>
  );
}

export default App;